import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getOrCreateGuestId, shouldEnablePostHog } from "@/lib/posthog";
import { env } from "@/env";

type PostHogStub = {
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
};

type PostHogClient = PostHogStub & { __real?: true };

const noopClient: PostHogClient = {
  capture: () => {},
  identify: () => {},
};

const PostHogContext = createContext<PostHogClient>(noopClient);

let latestClient: PostHogClient = noopClient;

export function getPostHog(): PostHogClient {
  return latestClient;
}

export function usePostHog(): PostHogClient {
  return useContext(PostHogContext);
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<PostHogClient>(noopClient);

  useEffect(() => {
    const enabled = shouldEnablePostHog(
      import.meta.env.DEV,
      env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN,
    );
    if (!enabled) return;

    let cancelled = false;

    const start = () => {
      void import("posthog-js").then(({ default: posthog }) => {
        if (cancelled) return;
        posthog.init(env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ?? "", {
          api_host: "/ingest",
          ui_host: env.VITE_PUBLIC_POSTHOG_HOST || "https://us.posthog.com",
          defaults: "2025-05-24",
          debug: false,
        });
        posthog.identify(getOrCreateGuestId(), { anonymous: true });
        const client: PostHogClient = {
          capture: (event, props) => posthog.capture(event, props),
          identify: (id, props) => posthog.identify(id, props),
          __real: true,
        };
        latestClient = client;
        setClient(client);
        try {
          if (!sessionStorage.getItem("trw_session_logged")) {
            sessionStorage.setItem("trw_session_logged", "1");
            posthog.capture("app_session_started");
          }
        } catch {
          // Storage unavailable — skip.
        }
      });
    };

    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (typeof idleWindow.requestIdleCallback === "function") {
      const id = idleWindow.requestIdleCallback(start, { timeout: 3000 });
      return () => {
        cancelled = true;
        idleWindow.cancelIdleCallback?.(id);
      };
    }

    const t = window.setTimeout(start, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);

  return <PostHogContext.Provider value={client}>{children}</PostHogContext.Provider>;
}
